import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { MOVED_TO_PLATFORM, MOVED_TO_SECTOR } from './sector/pages'
import { OnlyIn, SectorRedirect, SectorScope } from './sector/SectorScope'
import { useAuthBootstrap, useAuthStore } from '@lipaflow/shared'
import AdminLayout from './components/AdminLayout'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import DashboardPage from './pages/DashboardPage'
import TenantsPage from './pages/TenantsPage'
import OnboardingPage from './pages/OnboardingPage'
import VerificationQueuePage from './pages/VerificationQueuePage'
import DiscountsPage from './pages/DiscountsPage'
import BillingApprovalsPage from './pages/BillingApprovalsPage'
import OffboardingPage from './pages/OffboardingPage'
import BillingHealthPage from './pages/BillingHealthPage'
import PricesPage from './pages/PricesPage'
import SettingsPage from './pages/SettingsPage'
import CurriculumQaPage from './pages/CurriculumQaPage'
import CurriculumViewerPage from './pages/CurriculumViewerPage'
import IntegrationsPage from './pages/IntegrationsPage'
import RolesPage from './pages/RolesPage'
import VerticalsPage from './pages/VerticalsPage'
import AuditLogsPage from './pages/AuditLogsPage'
import AdminWizardPage from './pages/wizard/AdminWizardPage'
import SupportPage from './pages/SupportPage'

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

/** An old onboarding link: the customer's own sector is found by the wizard. */
function MovedOnboarding() {
  const { tenantId } = useParams()
  return <SectorRedirect page={`onboarding/${tenantId}`} />
}

function SectorHome() {
  const { sector } = useParams()
  return <Navigate to={`/${sector}`} replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const { isValidating } = useAuthBootstrap({ loadTenant: false })

  if (isValidating) {
    return <PageLoader />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!user?.permissions?.includes('platform:admin')) {
      return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      
      <Route path="/" element={
        <ProtectedRoute>
            <AdminLayout />
        </ProtectedRoute>
      }>
        {/* The sector last worked in, else the first (admin sector workspaces). */}
        <Route index element={<SectorRedirect />} />

        {/* Platform-wide: not any one sector's. */}
        <Route path="platform/sectors" element={<VerticalsPage />} />
        <Route path="platform/roles" element={<RolesPage />} />
        <Route path="platform/activity" element={<AuditLogsPage />} />
        <Route path="platform/account" element={<SettingsPage />} />

        {/* Old addresses keep working. */}
        {Object.entries(MOVED_TO_SECTOR).map(([from, to]) => (
          <Route key={from} path={from} element={<SectorRedirect page={to} />} />
        ))}
        <Route path="onboarding/new" element={<SectorRedirect page="onboarding/new" />} />
        <Route path="onboarding/:tenantId" element={<MovedOnboarding />} />
        <Route path="wizard" element={<SectorRedirect page="onboarding/new" />} />
        <Route path="wizard/:tenantId" element={<MovedOnboarding />} />
        {Object.entries(MOVED_TO_PLATFORM).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={`/platform/${to}`} replace />} />
        ))}

        {/* One sector's workspace. */}
        <Route path=":sector" element={<SectorScope />}>
          <Route index element={<DashboardPage />} />
          <Route path="customers" element={<TenantsPage />} />
          {/* One onboarding area: the list, and setting a customer up within it. */}
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="onboarding/new" element={<AdminWizardPage />} />
          <Route path="onboarding/:tenantId" element={<AdminWizardPage />} />
          <Route path="verifications" element={<VerificationQueuePage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="curriculum-review" element={<OnlyIn sectors={['school']}><CurriculumQaPage /></OnlyIn>} />
          <Route path="curriculum-library" element={<OnlyIn sectors={['school']}><CurriculumViewerPage /></OnlyIn>} />
          <Route path="prices" element={<PricesPage />} />
          <Route path="discounts" element={<DiscountsPage />} />
          <Route path="billing-health" element={<BillingHealthPage />} />
          <Route path="billing-approvals" element={<BillingApprovalsPage />} />
          <Route path="offboarding" element={<OffboardingPage />} />
          <Route path="payment-connections" element={<IntegrationsPage />} />
          <Route path="activity" element={<AuditLogsPage />} />
          <Route path="*" element={<SectorHome />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
